import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';
import { asyncForEach, getCleanedUrlParams, getUrl, showError } from '@utils';
import { QUERY_KEYS } from 'constants/queryKeys';
import { URLS } from 'layouts/DrawerLayout/constants';
import { insurancesServices } from '@services';
import { FILTER_RULE_ENUMS, LIMIT } from '@constants';
import { handleInsurancesData } from './utils';

export const INSURANCES_PAGE_SIZE = LIMIT;
const EMPTY_RULE = {
  type: FILTER_RULE_ENUMS.EmptyRuleType,
};

export const useInsurancesOperations = (fetchOnMount) => {
  const router = useRouter();
  const {
    query: {
      page = 1,
      pageSize = INSURANCES_PAGE_SIZE,
      orderBy = 'internalId',
      order = 'asc',
      query: searchQuery,
    },
    isReady,
  } = router;

  const cleanParams = getCleanedUrlParams({
    page,
    orderBy,
    order,
    query: searchQuery,
    pageSize: parseInt(pageSize, 10) || INSURANCES_PAGE_SIZE,
  });
  const insurancesResponse = useQuery(
    [QUERY_KEYS.GET_INSURANCES_KEY, cleanParams],

    () =>
      insurancesServices.filterInsurances({
        params: cleanParams,
        payload: EMPTY_RULE,
      }),
    { enabled: fetchOnMount && isReady }
  );

  useEffect(() => {
    if (insurancesResponse?.error) {
      showError(insurancesResponse?.error);
    }
  }, [insurancesResponse?.error]);

  const handleDeleteInsurances = async (selectedInsurances) => {
    try {
      await asyncForEach(selectedInsurances, async (insuranceId) => {
        await insurancesServices.deleteInsurance(insuranceId);
      });

      insurancesResponse.refetch();
    } catch (error) {
      showError(error);
    }
  };

  const handlePageChange = (newPage, newPageSize) => {
    const currentPage = router.query.page || '1';
    const currentPageSize =
      router.query.pageSize || INSURANCES_PAGE_SIZE.toString();

    const urlParams = { ...router.query };

    if (newPage !== currentPage) {
      urlParams.page = newPage;
    }

    if (newPageSize && newPageSize.toString() !== currentPageSize) {
      urlParams.pageSize = newPageSize;
      urlParams.page = 1;
    }

    router.push(getUrl(urlParams, URLS.insurances));
  };

  const handleSortBy = (sortByModel) => {
    const url = getUrl({ ...router.query, ...sortByModel }, URLS.insurances);

    router.push(url);
  };

  const insurances = handleInsurancesData(insurancesResponse?.data?.data || []);
  return {
    page,
    total: insurancesResponse?.data?.meta?.total,
    loading: insurancesResponse?.isLoading,
    insurances,
    pageSize: parseInt(pageSize, 10) || INSURANCES_PAGE_SIZE,
    hasErrors: !!insurancesResponse?.error,
    errorMessage: insurancesResponse?.error,
    getInsurances: insurancesResponse?.refetch,
    handlePageChange,
    deleteInsurances: handleDeleteInsurances,
    handleSortBy,
  };
};
